// src/components/shared/modals/OverrideRequestModal.tsx
// W25 lean override slice — a student records a justified, evidenced exception for
// an unsatisfied requirement. Single-actor: it takes effect immediately. Honest:
// the audit still shows the raw status alongside "satisfied via override".
import React, { useState } from 'react';
import { ShieldCheck } from 'lucide-react';

import BaseModal from './BaseModal';
import { EvidenceType, OverrideDraft } from '../../../services/overrideService';

const EVIDENCE_OPTIONS: { value: EvidenceType; label: string }[] = [
  { value: 'self_declaration', label: 'Self-declaration' },
  { value: 'transfer_credit', label: 'Transfer credit' },
  { value: 'placement_test', label: 'Placement test' },
  { value: 'instructor_consent', label: 'Instructor consent' },
  { value: 'advisor_email', label: 'Advisor email' },
  { value: 'department_letter', label: 'Department letter' },
];

const MIN_JUSTIFICATION = 10;

interface OverrideRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The requirement's stable id (its requirement_code). */
  constraintId: string;
  requirementName?: string;
  onSubmit: (draft: OverrideDraft) => Promise<void>;
}

const OverrideRequestModal = ({
  isOpen,
  onClose,
  constraintId,
  requirementName,
  onSubmit,
}: OverrideRequestModalProps) => {
  const [justification, setJustification] = useState('');
  const [evidenceType, setEvidenceType] = useState<EvidenceType>('self_declaration');
  const [evidenceRef, setEvidenceRef] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const valid = justification.trim().length >= MIN_JUSTIFICATION;

  const reset = () => {
    setJustification('');
    setEvidenceType('self_declaration');
    setEvidenceRef('');
    setExpiresAt('');
    setError(null);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (!valid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({
        constraint_id: constraintId,
        scope: 'requirement',
        justification: justification.trim(),
        evidence_type: evidenceType,
        evidence_ref: evidenceRef.trim() || null,
        expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
      });
      reset();
      onClose();
    } catch {
      setError('Failed to create override. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Override requirement"
      subtitle={requirementName}
      isLoading={submitting}
      icon={{ Component: ShieldCheck, bgColor: 'bg-accent bg-opacity-10', color: 'text-accent' }}
    >
      <div className="px-6 py-4 space-y-4">
        <p className="text-sm text-gray-600">
          Record a justified exception for this requirement. It takes effect immediately and shows
          in your audit as <span className="font-medium">satisfied via override</span> — your
          underlying status stays visible.
        </p>

        <div>
          <label htmlFor="override-justification" className="block text-sm font-medium text-gray-700 mb-1">
            Justification <span className="text-red-500">*</span>
          </label>
          <textarea
            id="override-justification"
            rows={3}
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
            placeholder="Why does this requirement not apply, or how is it otherwise met?"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:border-primary"
          />
          <p className="text-xs text-gray-400 mt-1">
            {justification.trim().length}/{MIN_JUSTIFICATION} characters minimum
          </p>
        </div>

        <div>
          <label htmlFor="override-evidence-type" className="block text-sm font-medium text-gray-700 mb-1">
            Evidence type
          </label>
          <select
            id="override-evidence-type"
            value={evidenceType}
            onChange={(e) => setEvidenceType(e.target.value as EvidenceType)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:border-primary"
          >
            {EVIDENCE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="override-evidence-ref" className="block text-sm font-medium text-gray-700 mb-1">
            Evidence reference <span className="text-gray-400">(optional)</span>
          </label>
          <input
            id="override-evidence-ref"
            type="text"
            value={evidenceRef}
            onChange={(e) => setEvidenceRef(e.target.value)}
            placeholder="URL or document reference"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:border-primary"
          />
        </div>

        <div>
          <label htmlFor="override-expires" className="block text-sm font-medium text-gray-700 mb-1">
            Expires <span className="text-gray-400">(optional)</span>
          </label>
          <input
            id="override-expires"
            type="date"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:border-primary"
          />
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>

      <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
        <button
          type="button"
          onClick={handleClose}
          disabled={submitting}
          className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!valid || submitting}
          className="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-opacity-90 transition-colors disabled:opacity-50"
        >
          {submitting ? 'Saving…' : 'Apply override'}
        </button>
      </div>
    </BaseModal>
  );
};

export default OverrideRequestModal;
